import React, { useEffect, useMemo, useRef, useState } from 'react';

import { GrupoDeAcessoDetalhe } from '../../../api/gruposDeAcessoApi';
import { User } from '../../../api/usersApi';
import '../../../shared/ui/modal-close-button.css';
import '../pages/css/adminSistema.css';
import './css/grupoModal.css';

interface Props {
  grupo: GrupoDeAcessoDetalhe;
  usuarios: User[];
  onFechar: () => void;
  onSalvar: (id: string, usuarioIds: string[]) => void;
}

/**
 * Quem está no grupo (at-81, ADR 0044).
 *
 * DECLARATIVO, como o backend: a lista marcada substitui o conjunto inteiro. Por isso o estado
 * inicial precisa refletir exatamente quem já está lá — reabrir em branco e salvar esvaziaria o
 * grupo em silêncio (a classe do BUG-V43).
 */
const UsuariosDoGrupoModal: React.FC<Props> = ({ grupo, usuarios, onFechar, onSalvar }) => {
  const overlayRef = useRef<HTMLDivElement>(null);
  const iniciais = useMemo(() => grupo.usuarios.map((u) => u.id), [grupo.usuarios]);
  const [selecionados, setSelecionados] = useState<string[]>(iniciais);
  const [busca, setBusca] = useState('');
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') onFechar();
    };
    document.addEventListener('keydown', aoTeclar);
    return () => document.removeEventListener('keydown', aoTeclar);
  }, [onFechar]);

  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    const ativos = usuarios.filter((u) => !u.deletedAt);
    if (!termo) return ativos;
    return ativos.filter((u) =>
      `${u.fullName ?? ''} ${u.username ?? ''}`.toLowerCase().includes(termo),
    );
  }, [usuarios, busca]);

  const alternar = (id: string) => {
    setSelecionados((atuais) =>
      atuais.includes(id) ? atuais.filter((u) => u !== id) : [...atuais, id],
    );
  };

  /** Quantos entram e quantos saem — o resumo que evita salvar uma remoção sem perceber. */
  const entram = selecionados.filter((id) => !iniciais.includes(id)).length;
  const saem = iniciais.filter((id) => !selecionados.includes(id)).length;

  return (
    <div
      className="grupo-modal-overlay"
      ref={overlayRef}
      onMouseDown={(evento) => {
        if (evento.target === overlayRef.current) onFechar();
      }}
    >
      <div className="grupo-modal" role="dialog" aria-modal="true">
        <div className="grupo-modal-header">
          <h2>Pessoas em "{grupo.nome}"</h2>
          <button type="button" className="close-button" onClick={onFechar} aria-label="Fechar">
            ×
          </button>
        </div>

        <div className="grupo-modal-body">
          <p className="grupo-modal-hint">
            Quem estiver marcado recebe os perfis do grupo
            {grupo.papeisRotulos.length > 0 ? ` (${grupo.papeisRotulos.join(', ')})` : ''}. A
            mudança passa a valer no próximo login de cada pessoa.
          </p>

          {!grupo.ativo && (
            <p className="grupo-modal-hint grupo-modal-hint-alerta">
              Este grupo está inativo: enquanto isso, ninguém recebe os perfis dele.
            </p>
          )}

          <div className="grupo-modal-campo">
            <label htmlFor="grupo-busca-pessoa">Buscar pessoa</label>
            <input
              id="grupo-busca-pessoa"
              className="admin-input"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Nome ou usuário"
              autoFocus
            />
          </div>

          <div className="grupo-modal-pessoas">
            {visiveis.length === 0 ? (
              <p className="grupo-modal-vazio">
                {busca.trim()
                  ? 'Nenhuma pessoa encontrada com esse nome.'
                  : 'Nenhuma pessoa ativa no cadastro.'}
              </p>
            ) : (
              visiveis.map((usuario) => {
                const marcado = selecionados.includes(usuario.id);
                const nome = usuario.fullName || usuario.username;
                return (
                  <label
                    key={usuario.id}
                    className={`grupo-modal-perfil${marcado ? ' grupo-modal-perfil-marcado' : ''}`}
                    title={nome}
                  >
                    <input type="checkbox" checked={marcado} onChange={() => alternar(usuario.id)} />
                    {nome}
                  </label>
                );
              })
            )}
          </div>

          <p className="grupo-modal-contador">
            {selecionados.length} pessoa(s) no grupo
            {entram > 0 || saem > 0
              ? ` — ${entram} a adicionar, ${saem} a remover ao salvar.`
              : '. Nada mudou até agora.'}
          </p>

          {saem > 0 && (
            <p className="grupo-modal-hint grupo-modal-hint-alerta">
              Quem sair do grupo perde os perfis dele no próximo login.
            </p>
          )}
        </div>

        <div className="grupo-modal-footer">
          <button type="button" className="admin-btn admin-btn-secondary" onClick={onFechar}>
            Cancelar
          </button>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            disabled={salvando}
            onClick={() => {
              setSalvando(true);
              onSalvar(grupo.id, selecionados);
              setSalvando(false);
            }}
          >
            {salvando ? 'Salvando...' : 'Salvar pessoas'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UsuariosDoGrupoModal;
